import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Search, X, Check, Building2, User, ChevronsUpDown, FileText, ArrowRight, Loader2 } from 'lucide-react';
import { sanitizeText } from '../../utils/textSanitizer';

export interface PedidoClienteOption {
  id: number; // ID do Pedido
  pessoa_id?: number;
  nome_cliente: string;
  nome_fantasia?: string;
  documento?: string;
  fone?: string;
  email?: string;
}

interface ClientePedidoSearchSelectProps {
  value: string | number; // ID do Pedido selecionado
  onChange: (pedidoId: string, option?: PedidoClienteOption) => void;
  options: PedidoClienteOption[];
  loading?: boolean;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
}

export const ClientePedidoSearchSelect: React.FC<ClientePedidoSearchSelectProps> = ({
  value,
  onChange,
  options,
  loading = false,
  required = false,
  disabled = false,
  placeholder = 'Digite o nome do cliente, nome fantasia, documento ou Nº pedido...',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  // Pedido / Cliente atualmente selecionado
  const selectedOption = useMemo(() => {
    if (!value) return null;
    return options.find((opt) => String(opt.id) === String(value)) || null;
  }, [value, options]);

  // Fecha popover ao clicar fora
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Foca no input quando o dropdown se abre
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 50);
    }
  }, [isOpen]);

  // Lista filtrada em tempo real
  const filteredOptions = useMemo(() => {
    if (!searchTerm.trim()) {
      return options;
    }

    const term = searchTerm.toLowerCase().trim();
    const cleanNumbers = term.replace(/\D/g, '');

    return options.filter((opt) => {
      const nome = (opt.nome_cliente || '').toLowerCase();
      const fantasia = (opt.nome_fantasia || '').toLowerCase();
      const doc = (opt.documento || '').toLowerCase();
      const docClean = (opt.documento || '').replace(/\D/g, '');
      const pedidoIdStr = String(opt.id);

      const matchNome = nome.includes(term);
      const matchFantasia = fantasia.includes(term);
      const matchDoc = doc.includes(term) || (cleanNumbers.length > 0 && docClean.includes(cleanNumbers));
      const matchPedido = pedidoIdStr.includes(term) || term.includes(pedidoIdStr);

      return matchNome || matchFantasia || matchDoc || matchPedido;
    });
  }, [options, searchTerm]);

  // Reseta índice destacado quando a lista muda
  useEffect(() => {
    setHighlightedIndex(0);
  }, [filteredOptions.length]);

  // Rola o item destacado para a visão se navegando por teclado
  useEffect(() => {
    if (isOpen && listRef.current && listRef.current.children[highlightedIndex]) {
      const itemElement = listRef.current.children[highlightedIndex] as HTMLElement;
      itemElement.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedIndex, isOpen]);

  const handleSelect = (option: PedidoClienteOption) => {
    onChange(String(option.id), option);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleClear = (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    onChange('');
    setSearchTerm('');
    setIsOpen(true);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;

    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex((prev) => (prev < filteredOptions.length - 1 ? prev + 1 : prev));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filteredOptions.length > 0 && filteredOptions[highlightedIndex]) {
        handleSelect(filteredOptions[highlightedIndex]);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setIsOpen(false);
    }
  };

  // Helper para formatar CPF/CNPJ de forma limpa
  const formatDocument = (doc?: string) => {
    if (!doc) return null;
    const clean = doc.replace(/\D/g, '');
    if (clean.length === 11) {
      return clean.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    }
    if (clean.length === 14) {
      return clean.replace(/(\d{2})(\d{3})(\d{3})(\d{4})/, '$1.$2.$3/$4-$5');
    }
    return doc;
  };

  return (
    <div className="relative w-full" ref={containerRef} onKeyDown={handleKeyDown}>
      {/* Estado: Cliente Selecionado */}
      {selectedOption && !isOpen ? (
        <div
          onClick={() => !disabled && setIsOpen(true)}
          className={`w-full flex items-center justify-between p-2.5 bg-gradient-to-r from-slate-50 to-white border ${
            disabled ? 'border-slate-200 bg-slate-100 cursor-not-allowed' : 'border-slate-300 hover:border-red-400 cursor-pointer shadow-2xs'
          } rounded-xl transition-all group`}
        >
          <div className="flex items-center gap-3 min-w-0 pr-2">
            <div className="w-8 h-8 rounded-lg bg-red-100 text-red-600 flex items-center justify-center shrink-0 border border-red-200">
              <Building2 className="w-4 h-4" />
            </div>
            <div className="min-w-0 text-left">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-bold text-slate-900 truncate">
                  {sanitizeText(selectedOption.nome_cliente)}
                </span>
                <span className="px-1.5 py-0.5 rounded-md bg-slate-200/80 text-slate-700 text-3xs font-mono font-bold shrink-0">
                  Pedido #{selectedOption.id}
                </span>
              </div>
              <div className="flex items-center gap-2 text-3xs text-slate-500 truncate mt-0.5">
                {selectedOption.nome_fantasia && (
                  <span className="text-slate-600 font-medium truncate">
                    ({sanitizeText(selectedOption.nome_fantasia)})
                  </span>
                )}
                {selectedOption.documento && (
                  <span className="font-mono text-slate-500">
                    Doc: {formatDocument(selectedOption.documento)}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-1 shrink-0">
            {!disabled && (
              <button
                type="button"
                onClick={handleClear}
                className="p-1 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                title="Limpar cliente e buscar outro"
              >
                <X className="w-4 h-4" />
              </button>
            )}
            <div className="px-2 py-1 bg-slate-100 group-hover:bg-red-50 text-slate-600 group-hover:text-red-700 rounded-md text-2xs font-semibold transition-colors flex items-center gap-1">
              <span>Trocar</span>
              <ChevronsUpDown className="w-3 h-3 text-slate-400" />
            </div>
          </div>
        </div>
      ) : (
        /* Estado: Campo de Busca com Input Ativo */
        <div className="relative">
          <div className="relative flex items-center">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin text-red-500" />
              ) : (
                <Search className="w-4 h-4 text-red-500" />
              )}
            </div>

            <input
              ref={inputRef}
              type="text"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                if (!isOpen) setIsOpen(true);
              }}
              onFocus={() => setIsOpen(true)}
              disabled={disabled}
              placeholder={placeholder}
              className="w-full pl-9 pr-20 py-2.5 text-sm bg-white border border-slate-300 focus:border-red-500 focus:ring-2 focus:ring-red-500/20 rounded-xl outline-none transition-all placeholder:text-slate-400 text-slate-900 font-medium"
            />

            <div className="absolute inset-y-0 right-0 pr-2.5 flex items-center gap-1">
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm('')}
                  className="p-1 text-slate-400 hover:text-slate-600 rounded-md"
                  title="Limpar busca"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
              <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className="p-1 text-slate-400 hover:text-slate-600 rounded-md"
              >
                <ChevronsUpDown className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Popover / Dropdown Flutuante com Resultados Filtrados */}
      {isOpen && (
        <div className="absolute z-50 left-0 right-0 mt-1.5 bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden animate-in fade-in zoom-in-95 duration-100">
          <div className="p-2 bg-slate-50 border-b border-slate-200/80 flex items-center justify-between text-2xs text-slate-500 font-medium">
            <span>
              {filteredOptions.length === 0
                ? 'Nenhum cliente encontrado'
                : `Exibindo ${filteredOptions.length} de ${options.length} clientes/pedidos`}
            </span>
            <span className="text-3xs text-slate-400">Pressione ↑ ↓ para navegar, Enter para escolher</span>
          </div>

          <ul ref={listRef} className="max-h-64 overflow-y-auto divide-y divide-slate-100 p-1">
            {loading ? (
              <li className="p-4 text-center text-xs text-slate-500 flex items-center justify-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin text-red-500" />
                Carregando clientes...
              </li>
            ) : filteredOptions.length === 0 ? (
              <li className="p-6 text-center text-xs text-slate-500">
                <Building2 className="w-6 h-6 text-slate-300 mx-auto mb-1.5" />
                Nenhum cliente ou pedido corresponde a "<strong>{searchTerm}</strong>".
              </li>
            ) : (
              filteredOptions.map((opt, index) => {
                const isSelected = String(opt.id) === String(value);
                const isHighlighted = index === highlightedIndex;

                return (
                  <li
                    key={opt.id}
                    onClick={() => handleSelect(opt)}
                    onMouseEnter={() => setHighlightedIndex(index)}
                    className={`p-2.5 rounded-lg text-left cursor-pointer transition-colors flex items-center justify-between gap-3 ${
                      isHighlighted
                        ? 'bg-red-50/80 text-slate-900'
                        : isSelected
                        ? 'bg-slate-50 text-slate-900'
                        : 'hover:bg-slate-50 text-slate-800'
                    }`}
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div
                        className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${
                          isSelected
                            ? 'bg-red-600 text-white'
                            : 'bg-slate-100 text-slate-600 border border-slate-200'
                        }`}
                      >
                        <Building2 className="w-3.5 h-3.5" />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-xs font-bold text-slate-900 truncate">
                            {sanitizeText(opt.nome_cliente)}
                          </p>
                          <span className="px-1.5 py-0.2 bg-slate-200 text-slate-700 text-3xs font-mono font-bold rounded shrink-0">
                            Pedido #{opt.id}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-3xs text-slate-500 truncate mt-0.5">
                          {opt.nome_fantasia && (
                            <span className="text-slate-600 font-medium truncate">
                              Fantasia: {sanitizeText(opt.nome_fantasia)}
                            </span>
                          )}
                          {opt.documento && (
                            <span className="font-mono text-slate-400 shrink-0">
                              Doc: {formatDocument(opt.documento)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="shrink-0 flex items-center gap-1.5">
                      {isSelected && <Check className="w-4 h-4 text-red-600" />}
                      <span className="text-3xs text-slate-400 font-semibold uppercase">
                        Selecionar
                      </span>
                    </div>
                  </li>
                );
              })
            )}
          </ul>
        </div>
      )}
    </div>
  );
};
